import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import type { AdminOrgProject, AscStoreLink } from "@a7y/types";
import { Check, Copy, FolderOpen } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROJECT_STATUS_COLORS } from "./constants";
import { EditVendorNumberDialog } from "./EditVendorNumberDialog";
import { Section } from "./shared";

export interface ProjectsTabProps {
  orgId: string;
  projects: AdminOrgProject[];
}

function derivePlatform(apps?: AdminOrgProject["apps"]): string {
  if (!apps) return "\u2014";
  const platforms: string[] = [];
  if (apps.asc) platforms.push("App Store");
  if (apps.gpc) platforms.push("Play Store");
  return platforms.length > 0 ? platforms.join(", ") : "\u2014";
}

function getAscStoreLink(
  project: AdminOrgProject,
): AscStoreLink | undefined {
  return project.apps?.asc?.store_link as AscStoreLink | undefined;
}

export function ProjectsTab({ orgId, projects }: ProjectsTabProps) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<{
    projectId: string;
    currentVendorNumber?: string;
  } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyId = (projectId: string) => {
    navigator.clipboard.writeText(projectId);
    setCopiedId(projectId);
    setTimeout(() => setCopiedId((current) => (current === projectId ? null : current)), 2000);
  };

  return (
    <Section title={`Projects (${projects.length})`} icon={<FolderOpen className="h-5 w-5" />}>
      <Table className="[--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]">
        <TableHead>
          <TableRow>
            <TableHeader>Project</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Languages</TableHeader>
            <TableHeader>Platform</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader>ASC Vendor Number</TableHeader>
            <TableHeader className="w-px"></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                <Text className="text-zinc-500">No projects found</Text>
              </TableCell>
            </TableRow>
          ) : (
            projects.map((project) => {
              const ascStoreLink = getAscStoreLink(project);
              const hasAscLink = !!ascStoreLink;
              const vendorNumber = ascStoreLink?.vendor_number;
              const isCopied = copiedId === project.id;
              return (
                <TableRow
                  key={project.id}
                  className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
                  onClick={() =>
                    navigate(`/admin/organizations/${orgId}/projects/${project.id}`)
                  }
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {project.iconUrl ? (
                        <img
                          src={project.iconUrl}
                          alt=""
                          className="h-8 w-8 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-700">
                          <FolderOpen className="h-4 w-4 text-zinc-400" />
                        </div>
                      )}
                      <Text className="font-medium">{project.name}</Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge color={PROJECT_STATUS_COLORS[project.status] ?? "zinc"}>
                      {project.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {project.targetLocales.length}
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {derivePlatform(project.apps)}
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {formatDate(project.createdAt)}
                  </TableCell>
                  <TableCell>
                    {hasAscLink ? (
                      <div className="flex items-center gap-3">
                        <Text className="text-zinc-500 font-mono text-xs">
                          {vendorNumber ?? "Not set"}
                        </Text>
                        <Button
                          plain
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setEditing({
                              projectId: project.id,
                              currentVendorNumber: vendorNumber,
                            });
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <Text className="text-zinc-400">{"\u2014"}</Text>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyId(project.id);
                      }}
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      title="Copy project ID"
                    >
                      {isCopied ? (
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <EditVendorNumberDialog
        open={!!editing}
        projectId={editing?.projectId ?? ""}
        orgId={orgId}
        currentVendorNumber={editing?.currentVendorNumber}
        onClose={() => setEditing(null)}
      />
    </Section>
  );
}
